/**
 * La configuration ajax commune aux appels GET et POST
 */
const AJAX_CONFIG = {
  crossDomain: true,
  xhrFields: {
    withCredentials: true,
  },
};

/**
 * La configuration ajax commune aux appels GET /version
 */
const AJAX_CONFIG_VERSION = {
  crossDomain: true,
  xhrFields: {
    withCredentials: false,
  },
};

/**
 * Service générique pour réaliser des appels ajax GET et POST au format json
 * @class
 * @constructor
 * @public
 * @property {string} baseUrl L'url du serveur de l'api.
 * @property {string} alivePath Le chemin vers le service à appeler pour vérifier l'état de vie,
 * @property {*} ajaxConfig La configuration ajax pour les appels GET et POST.
 */
class LncBaseApiClientService {
  /**
   * Initialise une instance de type {@type ApiClientBaseService}.
   * @private
   * @param baseUrl {string} L'url du serveur de l'api.
   * @param alivePath {string} Le chemin vers le service à appeler pour vérifier l'état de vie, le chemin doit commencer par une barre oblique.
   * @param xsrfEnabled {boolean} Flag permettant de positionner le token XSRF dans le header des requêtes de type POST
   */
  constructor(baseUrl, xsrfEnabled = true, alivePath = '/actuator/info') {
    this.baseUrl = baseUrl;
    this.alivePath = alivePath;
    this.xsrfEnabled = xsrfEnabled;
  }

  /**
   * Action pour tester l'état de vie de l'api.
   * @returns {Promise<bool>} La promesse d'une réponse.
   */
  isAlive() {
    return new Promise((resolve, reject) => {
      this.get(this.alivePath)
        .then(() => {
          resolve(true);
        })
        .catch(() => {
          reject();
        });
    });
  }

  /**
   * Action pour récupérer une ressource json avec la méthode GET.
   * @param path {string} Le chemin vers le service à appeler,
   * le chemin doit commencer par une barre oblique.
   * @returns {Promise<BaseApiClientResponse>} La promesse d'une réponse à l'action GET.
   */
  get(path) {
    return new Promise((resolve, reject) => {
      jQuery
        .ajax({ ...AJAX_CONFIG, url: this.baseUrl + path, method: 'GET', contentType: 'text/plain' })
        .done((response, textStatus, jqXHR) => {
          resolve({ content: response, status: jqXHR.status });
        })
        .fail(response => {
          console.error(response);
          reject(response);
        });
    });
  }

  /**
   * Action pour appeler une uri /version avec la méthode GET.
   * @param path {string} Le chemin vers le service à appeler,
   * le chemin doit commencer par une barre oblique.
   * @returns {Promise<BaseApiClientResponse>} La promesse d'une réponse à l'action GET.
   */
  getVersion(path) {
    return new Promise((resolve, reject) => {
      jQuery
        .ajax({ ...AJAX_CONFIG_VERSION, url: this.baseUrl + path, method: 'GET', contentType: 'text/plain' })
        .done((response, textStatus, jqXHR) => {
          resolve({ content: response, status: jqXHR.status });
        })
        .fail(response => {
          console.error(response);
          reject(response);
        });
    });
  }

  /**
   * Action pour envoyer une ressource au format json avec la méthode POST.
   * @param path {string} Le chemin vers le service à appeler,
   * le chemin doit commencer par une barre oblique
   * @param data {*} La ressource qui sera envoyée au format json.
   * @returns {Promise<BaseApiClientResponse>} La promesse d'une réponse à l'action POST.
   */
  post(path, data) {
    return new Promise((resolve, reject) => {
      jQuery
        .ajax({
          ...AJAX_CONFIG,
          url: this.baseUrl + path,
          method: 'POST',
          data: JSON.stringify(data),
          contentType: 'application/json',
          headers: {
            ...(this.xsrfEnabled && { 'X-XSRF-TOKEN': this.getXsrfToken() }),
          },
        })
        .done((response, textStatus, jqXHR) => {
          resolve({ response: response, status: jqXHR.status });
        })
        .fail(response => {
          console.error(response);
          reject(response);
        });
    });
  }

  /**
   * Action pour envoyer une ressource au format json avec la méthode PUT.
   * @param path {string} Le chemin vers le service à appeler,
   * le chemin doit commencer par une barre oblique
   * @param data {*} La ressource qui sera envoyée au format json.
   * @returns {Promise<{response: *, status: number}|*>} La promesse d'une réponse à l'action PUT.
   */
  put(path, data) {
    return new Promise((resolve, reject) => {
      jQuery
        .ajax({
          ...AJAX_CONFIG,
          url: this.baseUrl + path,
          method: 'PUT',
          data: JSON.stringify(data),
          contentType: 'application/json',
          headers: {
            ...(this.xsrfEnabled && { 'X-XSRF-TOKEN': this.getXsrfToken() }),
          },
        })
        .done((response, textStatus, jqXHR) => {
          resolve({ response: response, status: jqXHR.status });
        })
        .fail(response => {
          console.error(response);
          reject(response);
        });
    });
  }

  /**
   * Recuperer la valeur de token XSRF.
   * @returns {String} La valeur de token XSRF.
   */
  getXsrfToken() {
    // Recuperer le token XSRF
    let xrfToken = document.cookie.match('(^|;) ?XSRF-TOKEN=([^;]*)(;|$)');

    if (!xrfToken) {
      console.error('Aucun token XSRF trouvé dans les cookies');
      return null;
    }

    return xrfToken[2];
  }
}

export { LncBaseApiClientService };
